import { useMutation } from '@apollo/client';
import { Visibility } from '@mui/icons-material';
import { Grid, Tooltip, IconButton } from '@mui/material';

import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { truncate } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import MARK_NOTIFICATION_AS_READ from 'gql/mutations/markNotificationAsRead.gql';

import ArticleTypeLogo from 'app/components/RichList/items/ArticleItem/components/ArticleTypeLogo';
import { translations } from 'locales/i18n';
import { ArticleNotification as IArticleNotification } from 'types/AppTypes';

interface Props {
  notification: IArticleNotification;
}

export default function ArticleNotification({ notification }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    refetchQueries: ['GetNotificationsForUser']
  });

  if (!notification.article) return null;

  return (
    <Grid
      container
      className={clsx('notifications', {
        'notifications--new': !notification.viewedAt
      })}
    >
      <Grid item className="notifications__logo">
        <ArticleTypeLogo articleType={notification.article.type} />
      </Grid>
      <Grid item xs>
        <Grid
          container
          onClick={() => {
            markNotificationAsRead({
              variables: { userId: notification.userId, notificationId: notification.id }
            }).finally(() => {
              navigate(`/articles/${notification.article?.id}`);
            });
          }}
        >
          <Grid container className="notifications__content" justifyContent="space-between">
            <Grid item xs className="notifications__content__headline">
              {truncate(notification.article.headline, {
                length: 60,
                separator: ' '
              })}
            </Grid>
            <Grid item xs="auto" className="notifications__content__date">
              <Tooltip
                title={new Date(notification.createdAt).toLocaleString()}
                placement="left"
                PopperProps={{ style: { zIndex: 2000 } }}
              >
                <span>
                  {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container className="notifications__description">
            {truncate(notification.article.shortDescription, {
              length: 130,
              separator: ' ',
              omission: ' [...]'
            })}
          </Grid>
        </Grid>
        <Grid
          container
          className="notifications__actions"
          justifyContent="flex-end"
          onClick={() => {
            markNotificationAsRead({
              variables: { userId: notification.userId, notificationId: notification.id }
            });
          }}
        >
          <Tooltip
            title={<>{t(translations.layout.profile.markAsRead())}</>}
            placement="left"
            PopperProps={{ style: { zIndex: 2000 } }}
          >
            <IconButton className="notifications__actions__mark-as-read">
              <Visibility />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
}
